import React, { Fragment, useEffect, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Order, Item, AppActions, AppTranslationHelper, OrderStatuses } from 'common/types';
import { MenuSwapTwoTone, PrinterPosTwoTone, SearchTwoTone } from 'common/icons';
import { Routes } from 'common/enums';
import { toast } from 'react-toastify';
import styles from './receipt.module.css';
import { appContext } from 'common/contexts';

type ReceiptProps = {
  isPrintable: boolean;
  items: Item[];
  onPrintCheck: (orderId: string | null) => void;
  onShowReceipts: () => void;
  order: Order | null;
  orderId: string | null;
  services: AppActions;
  translation: AppTranslationHelper;
};
let hasInitialized = false;
const Receipt: React.FC<ReceiptProps> = ({
  isPrintable,
  translation,
  orderId,
  order,
  items,
  services,
  onShowReceipts,
  onPrintCheck,
}) => {
  const [context] = useContext(appContext);

  // useEffect(() => {
  //   const checkAndCreateOrder = async () => {
  //     if (!hasInitialized) {
  //       // Intentar recuperar el ID de la orden abierta del localStorage
  //       const savedOrderId = localStorage.getItem('openOrderId');

  //       if (savedOrderId) {
  //         // Si hay una orden guardada, verificar que exista y esté abierta
  //         const savedOrder = context.orders.find((o: Order) => o.id === savedOrderId);
  //         if (savedOrder && savedOrder.status === OrderStatuses.Open) {
  //           services.orders.select(savedOrderId);
  //         } else {
  //           // Si la orden no existe o está cerrada, crear una nueva
  //           await services.orders.add();
  //         }
  //       } else {
  //         // Si no hay orden guardada, crear una nueva
  //         await services.orders.add();
  //       }
  //       hasInitialized = true;
  //     }
  //   };

  //   checkAndCreateOrder();
  // }, []); // <- array vacío para que solo se ejecute al montar

  // Crear nueva orden cuando se cierra una
  // useEffect(() => {
  //   if (order?.status === OrderStatuses.Closed) {
  //     // Limpiar el localStorage cuando la orden se cierra
  //     localStorage.removeItem('openOrderId');
  //     services.orders.add();
  //   } else if (order?.status === OrderStatuses.Open) {
  //     // Guardar el ID de la orden abierta en localStorage
  //     localStorage.setItem('openOrderId', order.id);
  //   }
  // }, [order?.status, order?.id]); // Se ejecuta cuando cambia el status o el id de la orden

  const getTotalAmount = () => {
    const total = order?.items.filter(e => validItem(e.id)).reduce((acc, item) => acc + item.quantity * item.price, 0.0) || 0;
    return formatFinancial(total);
  };
  // helpers
  const [t] = useTranslation();
  const { formatFinancial } = translation;
  const getItemName = (id: string) => {
    const item = items.find((item) => item.id === id);
    const name = item?.itemParentName && item?.name ? `${item?.itemParentName} - ${item?.name}` : item?.name || t('common.deletedItem');
    return name;
  }
  const validItem = (id: string) => !!items.find((item) => item.id === id);
  // Estado para el campo de búsqueda
  const [searchOrderNumber, setSearchOrderNumber] = useState('');

  // handlers
  const handleShowReceipts = () => onShowReceipts();
  const handleEditOrderItem = (orderItemId: string) => services.item.select(orderItemId);
  const handlePrint = () => onPrintCheck(orderId);

  // Función para buscar un recibo por número
  const handleSearchOrder = () => {
    if (!searchOrderNumber.trim()) return;

    // Buscar la orden con el número especificado
    const foundOrder = context.orders.find((o: Order) =>
      String(o.orderName).toLowerCase() === searchOrderNumber.toLowerCase()
    );

    if (foundOrder) {
      // Si la orden existe, seleccionarla
      services.orders.select(foundOrder.id);
      setSearchOrderNumber(''); // Limpiar el campo después de la búsqueda exitosa
    } else {
      // Si no se encuentra la orden, mostrar un mensaje de error
      toast.error(t('terminal.receipt.orderNotFound', 'Recibo no encontrado'));
    }
  };

  // assets
  const renderOrderItems = () => {
    if (!order) return null;
    return (
      <Fragment>
        <tbody>
          {order.items.map((item) => (
            <tr
              key={`item-${item.id}`}
              className={styles.row}
              onClick={() => handleEditOrderItem(item.id)}
              style={{ backgroundColor: validItem(item.id) ? '' : '#ffb3b3', cursor: validItem(item.id) ? 'pointer' : 'not-allowed' }}
            >
              <td className={`${styles.cell} ${styles.cellName}`}>
                {getItemName(item.id)} <span className={styles.ghostly}> {`x ${item.quantity}`}</span>
              </td>
              <td className={`${styles.cell} ${styles.cellPrice}`}>{formatFinancial(item.quantity * item.price)}</td>
            </tr>
          ))}
          {!!order.taxAmount && (
            <tr>
              <td className={`${styles.cell} ${styles.cellName}`}>{t('common.tax')}</td>
              <td className={`${styles.cell} ${styles.cellPrice}`}>{formatFinancial(order.taxAmount)}</td>
            </tr>
          )}
        </tbody>
      </Fragment>
    );
  };

  const chargeText = `${t('terminal.charge')} ${order && order.totalAmount ? formatFinancial(order.totalAmount) : ''}`;
  const isDisabledCharge = !order || !order.totalAmount;

  return (
    <div className={styles.root}>
      <div className={styles.actions}>
        <button className={styles.receiptsBtn} onClick={handleShowReceipts}>
          {order?.orderName ? t('common.receipt#', { val: order?.orderName }) : t('common.receipts')}
          <MenuSwapTwoTone className={styles.receiptsIcon} />
        </button>
        <div className={styles.searchContainer}>
          <input
            type="text"
            className={styles.searchInput}
            placeholder={t('terminal.receipt.searchByNumber', 'Buscar recibo')}
            value={searchOrderNumber}
            onChange={(e) => setSearchOrderNumber(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearchOrder()}
          />
          <button
            className={styles.searchButton}
            onClick={handleSearchOrder}
            title={t('terminal.receipt.search', 'Buscar')}
          >
            <SearchTwoTone />
          </button>
        </div>
      </div>
      <div className={styles.body}>
        <table className={styles.listing}>{renderOrderItems()}</table>
      </div>
      <div className={styles.foot}>
        {isPrintable && (
          <button
            className="btn btn-light mr-2"
            title={t('common.printTheReceipt')}
            disabled={!order?.items.length}
            onClick={() => handlePrint()}
          >
            <PrinterPosTwoTone />
          </button>
        )}
        <Link
          role="button"
          className={`btn btn-primary w-100 ${isDisabledCharge ? 'disabled' : ''}`}
          aria-disabled={isDisabledCharge}
          tabIndex={isDisabledCharge ? -1 : 0}
          to={Routes.TerminalOrderCharge.replace(':id', `${orderId}`)}
        >
          {chargeText}
        </Link>
      </div>
    </div>
  );
};

export default Receipt;
